<template>
  <LayoutMain>
    <template #header>
      <PageHeader :icon="Heart" :title="t('myLoves.title')">
        <template #action>
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <span>{{ likedUsers.length }} {{ t('myLoves.loves') }}</span>
          </div>
        </template>
      </PageHeader>
    </template>

    <!-- Loved Ones Grid -->
    <div class="bg-white rounded-lg shadow-sm p-4 space-y-4">
      <!-- Currently Exploring -->
      <section v-if="activeLovedOnes.length > 0">
        <h2 class="font-semibold text-rogue-deep-forest mb-3 flex items-center gap-2">
          <Flame class="h-5 w-5 text-rogue-vibrant-green" />
          {{ t('myLoves.currentlyExploring') }}
        </h2>
        <div class="grid grid-cols-4 gap-2">
          <LoveCard
            v-for="user in activeLovedOnes"
            :key="user.id"
            :user="user"
            @click="viewProfile(user.id)"
          />
        </div>
      </section>

      <!-- Online Now -->
      <section v-if="onlineLovedOnes.length > 0">
        <h2 class="font-semibold text-rogue-deep-forest mb-3 flex items-center gap-2">
          <Circle class="h-5 w-5 text-blue-600 fill-current" />
          {{ t('myLoves.onlineNow') }}
        </h2>
        <div class="grid grid-cols-4 gap-2">
          <LoveCard
            v-for="user in onlineLovedOnes"
            :key="user.id"
            :user="user"
            @click="viewProfile(user.id)"
          />
        </div>
      </section>

      <!-- All Loves -->
      <section>
        <h2 class="font-semibold text-rogue-deep-forest mb-3 flex items-center gap-2">
          <Heart class="h-5 w-5 text-pink-600" />
          {{ t('myLoves.allLoves') }}
        </h2>
        <div class="grid grid-cols-4 gap-2">
          <LoveCard
            v-for="user in sortedLikedUsers"
            :key="user.id"
            :user="user"
            :show-last-seen="true"
            @click="viewProfile(user.id)"
          />
        </div>
      </section>
    </div>

    <!-- Activity Feed from Loved Ones -->
    <div class="bg-white rounded-lg shadow-sm p-4 space-y-4">
      <h2 class="font-semibold text-rogue-deep-forest flex items-center gap-2">
        <MessageSquare class="h-5 w-5 text-rogue-vibrant-green" />
        {{ t('myLoves.updatesFromYourLoves') }}
      </h2>

      <div v-if="lovedOnesPosts.length > 0" class="space-y-3">
        <MiniPostCard
          v-for="post in lovedOnesPosts"
          :key="post.id"
          :post="post"
          :show-love-badge="true"
          @view-profile="viewProfile(post.user_id)"
        />
      </div>

      <div v-else class="text-center py-8 bg-gray-50 rounded-lg">
        <MessageSquare class="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p class="text-gray-600">
          {{ t('myLoves.noRecentUpdates') }}
        </p>
      </div>
    </div>
  </LayoutMain>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '../stores/user'
import { supabase } from '../lib/supabase'
import { Heart, Flame, Circle, MessageSquare } from 'lucide-vue-next'
import LayoutMain from '../components/ui/LayoutMain.vue'
import PageHeader from '../components/ui/PageHeader.vue'
import LoveCard from '../components/ui/LoveCard.vue'
import MiniPostCard from '../components/ui/MiniPostCard.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const userStore = useUserStore()
const { t } = useI18n()

// State
const likedUsers = ref([])
const lovedOnesPosts = ref([])
const refreshInterval = ref(null)
const subscriptions = ref([])

// Computed properties
const activeLovedOnes = computed(() =>
  likedUsers.value.filter(user => user.zin_in_spel && isExploringActive(user.zin_in_spel_until))
)

const onlineLovedOnes = computed(() =>
  likedUsers.value.filter(
    user => !user.zin_in_spel && isOnline(user.last_active) && !activeLovedOnes.value.includes(user)
  )
)

const sortedLikedUsers = computed(() => {
  return [...likedUsers.value].sort((a, b) => {
    // Sort by last active
    return new Date(b.last_active) - new Date(a.last_active)
  })
})

// Lifecycle
onMounted(async () => {
  await loadLovedOnes()
  await loadLovedOnesPosts()

  // Set up real-time subscriptions
  setupRealtimeSubscriptions()

  // Refresh every 30 seconds
  refreshInterval.value = setInterval(() => {
    loadLovedOnes()
    loadLovedOnesPosts()
  }, 30000)
})

onUnmounted(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
  }
  // Clean up subscriptions
  subscriptions.value.forEach(subscription => {
    subscription.unsubscribe()
  })
})

// Data loading
async function loadLovedOnes() {
  if (!userStore.likes.length) {
    await userStore.fetchLikes()
  }

  if (userStore.likes.length === 0) {
    likedUsers.value = []
    return
  }

  const { data, error } = await supabase
    .from('profiles')
    .select(
      `
      *,
      profile_photos(
        id,
        photo_url,
        is_thumbnail,
        order
      )
    `
    )
    .in('id', userStore.likes)
    .order('last_active', { ascending: false })

  if (!error) {
    likedUsers.value = data.map(user => ({
      ...user,
      profile_photos: user.profile_photos?.sort((a, b) => (a.order || 0) - (b.order || 0))
    }))
  }
}

async function loadLovedOnesPosts() {
  if (!userStore.likes.length) return

  const fourHoursAgo = new Date()
  fourHoursAgo.setHours(fourHoursAgo.getHours() - 4)

  const { data, error } = await supabase
    .from('feed_posts')
    .select(
      `
      *,
      profile:profiles(name, thumbnail_url)
    `
    )
    .in('user_id', userStore.likes)
    .gte('created_at', fourHoursAgo.toISOString())
    .order('created_at', { ascending: false })
    .limit(20)

  if (!error) {
    lovedOnesPosts.value = data
  }
}

// Real-time subscriptions
function setupRealtimeSubscriptions() {
  if (!userStore.likes.length) return

  // Subscribe to profile changes for liked users
  const profileSub = supabase
    .channel('loved-ones-profiles')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'profiles',
        filter: `id=in.(${userStore.likes.join(',')})`
      },
      payload => {
        handleProfileUpdate(payload)
      }
    )
    .subscribe()

  // Subscribe to new posts from liked users
  const postsSub = supabase
    .channel('loved-ones-posts')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'feed_posts',
        filter: `user_id=in.(${userStore.likes.join(',')})`
      },
      payload => {
        handleNewPost(payload)
      }
    )
    .subscribe()

  // Store subscriptions for cleanup
  subscriptions.value = [profileSub, postsSub]
}

// Real-time handlers
function handleProfileUpdate(payload) {
  const index = likedUsers.value.findIndex(u => u.id === payload.new.id)
  if (index !== -1) {
    likedUsers.value[index] = { ...likedUsers.value[index], ...payload.new }
  }
}

function handleNewPost(payload) {
  // Add to beginning of posts array
  lovedOnesPosts.value.unshift(payload.new)

  // Keep only last 20 posts
  if (lovedOnesPosts.value.length > 20) {
    lovedOnesPosts.value.pop()
  }
}

// Utility functions
function isExploringActive(until) {
  if (!until) return false
  return new Date(until) > new Date()
}

function isOnline(lastActive) {
  if (!lastActive) return false
  const fiveMinutesAgo = new Date()
  fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5)
  return new Date(lastActive) > fiveMinutesAgo
}

function viewProfile(userId) {
  router.push(`/user/${userId}`)
}
</script>

<style scoped>
/* Pulse animation for exploring users */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.exploring-pulse {
  animation: pulse 2s infinite;
}

/* Smooth transitions */
.love-card-enter-active,
.love-card-leave-active {
  transition: all 0.3s ease;
}

.love-card-enter-from {
  opacity: 0;
  transform: scale(0.9);
}

.love-card-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>
